import type { WithActiveBranchId } from '@app/api/shared';
import type { NClientPayments, NFiles, TModelRecordReceipt } from '@app/types';

export enum ClientPaymentsEndpoints {
  AddClientPayment = 'AddClientPayment',
  GetClientPayments = 'GetClientPayments',
  GetClientPaymentInfo = 'GetClientPaymentInfo',
  GetRecordReceipt = 'GetRecordReceipt',
  GetClientBalance = 'GetClientBalance',
  PayRecordsByClientBalance = 'PayRecordsByClientBalance',
  PayRecordsByClientPayment = 'PayRecordsByClientPayment',
  GetOrCreateYookassaPayment = 'GetOrCreateYookassaPayment',
  GetAnonymousPaymentState = 'GetAnonymousPaymentState',
  ConfirmAnonymousPayment = 'ConfirmAnonymousPayment',
  DeletePayment = 'DeletePayment',
  SearchPayments = 'SearchPayments',
  GetPaymentsSearchResult = 'GetPaymentsSearchResult',
  GetClientPaymentFiltersData = 'GetClientPaymentFiltersData',
  AddPaymentToPaymentAccount = 'AddPaymentToPaymentAccount',
  GetOperationsFiltersData = 'GetOperationsFiltersData',
  SearchOperations = 'SearchOperations',
  GetOperationsSearchResult = 'GetOperationsSearchResult',
  TopClientBalance = 'TopClientBalance',
  PrepayRecord = 'PrepayRecord',
  CreateAndPayRecord = 'CreateAndPayRecord',
  CorrectClientPayments = 'CorrectClientPayments',
  ExportToExcel = 'ExportToExcel',
  AddClientReturn = 'AddClientReturn',
}

export interface ClientPaymentActionRequest extends WithActiveBranchId {
  clientId: number;
  accountId: number;
  amount: number;
  comment?: string;
}

export interface AddClientPaymentRequest extends ClientPaymentActionRequest {
  paymentDate?: string;
}

export interface AddClientReturnRequest extends ClientPaymentActionRequest {
  returnDate?: string;
}

export interface GetClientPaymentInfoRequest extends WithActiveBranchId {
  paymentId: number;
}

export interface GetRecordReceiptRequest extends WithActiveBranchId {
  recordId: number;
}

export interface GetClientBalanceRequest extends WithActiveBranchId {
  clientId: number;
  data: number[];
}

export interface ModelPayByBalance {
  RecordIds: number[] | null;
  Amount: number | null;
}

export interface PayRecordsByClientBalanceRequest extends WithActiveBranchId {
  data: ModelPayByBalance;
}

export interface PayRecordsByClientPaymentRequest extends WithActiveBranchId {
  paymentId: number;
  data: number[];
}

export interface GetOrCreateYookassaPaymentRequest extends WithActiveBranchId {
  clientId: number;
  recordId: number;
  amount: number;
  paymentDate: string;
  comment: string;
  clientPaymentsId: number | null;
  paymentAccountId: number;
  paymentMethod: string | null;
}

// Анонимное подтверждение платежа: клиент открывает ссылку с одноразовым
// токеном и вводит только email для чека. Филиал, платёж и организацию бэк
// определяет по токену сам, поэтому ActiveBranchId здесь нет — запрос уходит
// без авторизации (us/10744).
export interface ConfirmAnonymousPaymentRequest {
  Token: string;
  Email: string;
}

// Статус платежа в ЮKassa, который бэк возвращает по токену анонимной
// ссылки (us/11183). Кроме перечисленных фронт не различает и просто уходит
// редиректом на ConfirmationUrl.
export enum YookassaAnonymousPaymentStatus {
  AwaitingEmail = 'awaiting_email',
  Succeeded = 'succeeded',
  Canceled = 'canceled',
}

// Тот же анонимный токен, без авторизации: фронт проверяет им состояние
// платежа перед тем, как показывать форму ввода email или редиректить на уже
// созданный платёж (us/11183).
export interface GetAnonymousPaymentStateRequest {
  token: string;
}

export interface DeletePaymentRequest extends WithActiveBranchId {
  paymentId: number;
  returnToBalance?: boolean;
}

export interface SearchPaymentsRequest extends WithActiveBranchId {
  data: NClientPayments.ModelPaymentSearchFilter;
}

export interface GetPaymentsSearchResultRequest extends WithActiveBranchId {
  searchId: number;
  page: number;
  rowsPerPage: number;
}

export type GetClientPaymentFiltersDataRequest = WithActiveBranchId;

export interface AddPaymentToPaymentAccountRequest extends WithActiveBranchId {
  data: NClientPayments.ModelPaymentInsertion;
}

export type GetOperationsFiltersDataRequest = WithActiveBranchId;

export interface SearchOperationsRequest extends WithActiveBranchId {
  data: NClientPayments.ModelOperationSearchFilter;
}

export interface GetOperationsSearchResultRequest extends WithActiveBranchId {
  searchId: number;
  page: number;
  rowsPerPage: number;
}

export interface TopClientBalanceRequest extends WithActiveBranchId {
  data: NClientPayments.ModelTopClientBalance;
}

export interface PrepayRecordRequest extends WithActiveBranchId {
  recordId: number;
  accountId: number;
  amount: number;
  paymentDate?: string;
  comment?: string;
  paymentMethod?: string;
}

export interface CreateAndPayRecordRequest extends WithActiveBranchId {
  recordId: number;
  accountId: number;
  amount: number;
  paymentDate?: string;
  comment?: string;
}

export interface CorrectClientPaymentsRequest extends WithActiveBranchId {
  data: number;
}

export interface ExportToExcelRequest extends WithActiveBranchId {
  searchId: number;
}

export interface GetClientPaymentsRequest extends WithActiveBranchId {
  clientId: number;
}

export interface PaymentDetailsDto {
  Id: number;
  Operation: string | null;
  Date: string;
  TimeCreated: string;
  Amount: number;
  ExternalId: string | null;
  UserId: number;
  PaymentAccountId: number;
  PaymentType: string | null;
  Status: string | null;
  RecordNumber: number | null;
  ClientId: number | null;
}

export interface ClientPaymentsModelYookassaPayment {
  PaymentId: number;
  Status: string | null;
  ConfirmationUrl: string | null;
  PaymentUrlQR: string | null;
}

export type AddClientPaymentResponse = NClientPayments.ModelClientPayment;
export type AddClientReturnResponse = NClientPayments.ModelClientPayment;
export type GetClientPaymentsResponse = NClientPayments.ModelClientPayment[];
export type GetClientPaymentInfoResponse = PaymentDetailsDto;
export type GetRecordReceiptResponse = TModelRecordReceipt;
export type GetClientBalanceResponse = NClientPayments.ModelBalance;
export type PayRecordsByClientBalanceResponse = TModelRecordReceipt;
export type PayRecordsByClientPaymentResponse = TModelRecordReceipt;
export type GetOrCreateYookassaPaymentResponse =
  ClientPaymentsModelYookassaPayment;
export type GetAnonymousPaymentStateResponse =
  ClientPaymentsModelYookassaPayment;
export type ConfirmAnonymousPaymentResponse =
  ClientPaymentsModelYookassaPayment;
export type DeletePaymentResponse = void;
// JSONbig parses long decimal JSON numbers as BigNumber objects.
export interface ParsedPaymentTotalAmount {
  toFixed(decimalPlaces: number): string;
}

export interface PaymentSearchResult
  extends NClientPayments.ModelPaymentSearchResult {
  TotalAmount?: number | ParsedPaymentTotalAmount | null;
}

export type SearchPaymentsResponse = PaymentSearchResult;
export type GetPaymentsSearchResultResponse = PaymentSearchResult;
export type GetClientPaymentFiltersDataResponse =
  NClientPayments.ModelClientPaymentFiltersData;
export type AddPaymentToPaymentAccountResponse =
  NClientPayments.ModelClientPayment;
export type GetOperationsFiltersDataResponse =
  NClientPayments.ModelOperationsFiltersData;
export type SearchOperationsResponse =
  NClientPayments.ModelOperationSearchResult;
export type GetOperationsSearchResultResponse =
  NClientPayments.ModelOperationSearchResult;
export type TopClientBalanceResponse = NClientPayments.ModelBalance;
export type PrepayRecordResponse = TModelRecordReceipt;
export type CreateAndPayRecordResponse = TModelRecordReceipt;
export type CorrectClientPaymentsResponse = void;
export type ExportToExcelResponse = NFiles.ModelFile;
